import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';

import { createEffect } from '../utils/create-effect';

import { HAS_ACTIVE_HIGHLIGHT_CLASS, SELECTIONS_FOCUS_BOX_ELEMENT_ID } from './constants';
import { type HighlightStyleManager } from './utils/highlight-style-manager';
import { syncRangeToHighlightsLayer } from './utils/sync-range-to-highlights-layer';

const HIGHLIGHTS_LAYER_Z_INDEX = 10;
const FOCUS_LAYER_Z_INDEX = 11;

/**
 * Creates the main effect that draws / renders highlights, focus, etc.
 */
export const createHighlightsEffect = createEffect(
  (styleManager: HighlightStyleManager, controller: HighlightsController) => {
    // ---------------------------------------------------------------------------
    // DOM
    // ---------------------------------------------------------------------------

    /**
     * The el with the id SCROLLABLE_INNER_CONTAINER_ID, which is the main
     * scrolling container on the databases app, rendered in react.
     */
    let scrollableContainer: HTMLElement | null = null;

    // ---------------------------------------------------------------------------
    // Renderers
    // ---------------------------------------------------------------------------

    /**
     * The renderer responsible for creating, updating, and removing the blue selection
     * highlight elements in the DOM.
     */
    let highlightsRenderer: HighlightsRenderer | null = null;

    /**
     * The renderer that is responsible for creating, updating, and removing the blue
     * focus box.
     */
    let focusRenderer: HighlightsRenderer | null = null;

    function ensureDependentElementsAndRenderer() {
      // Retry mounting. Safely attaches the container when the DOM is finally ready.
      // because it is possible that mount is first called when there is not table layout.
      controller.mount();

      if (!scrollableContainer) {
        scrollableContainer = controller.getScrollContainer();
      }

      if (!highlightsRenderer && scrollableContainer) {
        highlightsRenderer = controller.createLayer(HIGHLIGHTS_LAYER_Z_INDEX);
      }

      if (!focusRenderer && scrollableContainer) {
        focusRenderer = controller.createLayer(FOCUS_LAYER_Z_INDEX);

        focusRenderer.add(
          {
            id: SELECTIONS_FOCUS_BOX_ELEMENT_ID,
            anchor: {
              row: 0,
              col: 0,
            },
            lead: {
              row: 0,
              col: 0,
            },
          },
          'none',
          SELECTIONS_FOCUS_BOX_ELEMENT_ID
        );
      }

      return !!(highlightsRenderer && scrollableContainer && focusRenderer);
    }

    return {
      deltaTypes: [
        'selectionModified',
        'selectionReplaced',
        'focusChanged',
        'selectionPreview',
      ],

      setup() {
        styleManager.add();
      },

      dispose() {
        if (highlightsRenderer) {
          controller.removeLayer(highlightsRenderer);

          highlightsRenderer = null;
        }

        if (focusRenderer) {
          controller.removeLayer(focusRenderer);

          focusRenderer = null;
        }

        if (scrollableContainer) {
          scrollableContainer.classList.remove(HAS_ACTIVE_HIGHLIGHT_CLASS);
        }

        styleManager.remove();

        scrollableContainer = null;
      },

      onDelta(delta, state) {
        if (
          !ensureDependentElementsAndRenderer() ||
          !highlightsRenderer ||
          !focusRenderer ||
          !scrollableContainer
        ) {
          return;
        }

        if (state.mode === 'idle' || state.selections.length === 0) {
          scrollableContainer.classList.remove(HAS_ACTIVE_HIGHLIGHT_CLASS);
        } else {
          scrollableContainer.classList.add(HAS_ACTIVE_HIGHLIGHT_CLASS);
        }

        if (delta.kind === 'selectionPreview') {
          syncRangeToHighlightsLayer(highlightsRenderer, state.mode, delta.activeRange);

          return;
        }

        // Handle the focus first (the blue box)
        const focusChange = delta.kind === 'focusChanged' ? delta : delta.focusChange;

        if (focusChange) {
          const row = focusChange.next?.row.index ?? 0;
          const col = focusChange.next?.col.index ?? 0;

          focusRenderer.update(
            {
              id: SELECTIONS_FOCUS_BOX_ELEMENT_ID,
              anchor: { row, col },
              lead: { row, col },
            },
            focusChange.next ? 'cell' : 'none'
          );
        }

        // Handle Selections (The Blue Backgrounds)

        if (delta.kind === 'selectionReplaced') {
          highlightsRenderer.clear();

          for (const range of delta.selections) {
            syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
          }

          return;
        }

        if (delta.kind === 'selectionModified') {
          if (delta.rangeIdsToRemove) {
            for (const id of delta.rangeIdsToRemove) {
              highlightsRenderer.remove(id);
            }
          }

          if (delta.added) {
            for (const range of delta.added) {
              syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
            }
          }

          if (delta.updated) {
            for (const range of delta.updated) {
              syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
            }
          }
        }
      },
    };
  }
);
